import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useEffect } from "react";
import { useSkipper } from "./hooks";
import { getTableMeta } from "./tableModels";
import ActionButtons from "./GridComponents/ActionButtons";
import CustomTable from "./GridComponents/CustomTable";

interface DataGridProps {
  columns: any;
  rows: any;
  onRowSelectionChange?: (selection: any) => void;
  onRowClick?: (row: any) => void;
  onRowHover?: (row?: any) => void;
  filter?: string;
  includeSelection?: boolean;
  includePagination?: boolean;
  className?: string;
  isFixedTable?: boolean;
}

export const DataGrid = (props: DataGridProps) => {
  const {
    columns,
    rows,
    onRowClick,
    onRowHover,
    onRowSelectionChange,
    includeSelection,
    includePagination,
    className,
  } = props;
  const rerender = React.useReducer(() => ({}), {})[1];

  const [data, setData] = React.useState(rows);
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 4,
  });
  const [columnVisibility, setColumnVisibility] = React.useState({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [autoResetPageIndex, skipAutoResetPageIndex] = useSkipper();

  useEffect(() => {
    if (onRowSelectionChange) {
      onRowSelectionChange(rowSelection);
    }
  }, [rowSelection]);

  useEffect(() => {
    setData(rows);
  }, [rows]);

  const table = useReactTable({
    data,
    columns,
    defaultColumn: {
      size: 150,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: includePagination
      ? getPaginationRowModel()
      : undefined,
    getSortedRowModel: getSortedRowModel(),
    autoResetPageIndex,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: (e) => {
      console.log(e);
      setRowSelection(e);
    },
    onPaginationChange: setPagination,
    enableMultiRowSelection: true,
    // Provide our updateData function to our table meta
    meta: getTableMeta(setData, skipAutoResetPageIndex),
    state: {
      columnVisibility,
      rowSelection,
      pagination,
      globalFilter: props.filter,
    },
  });

  return (
    <>
      <div className={`max-w-full overflow-x-auto overflow-auto ${className}`}>
        <CustomTable
          table={table}
          onRowClick={onRowClick}
          onRowHover={onRowHover}
          isFixedTable={props.isFixedTable}
        />
      </div>
      <ActionButtons
        toggleAllRowsSelected={table.toggleAllRowsSelected}
        getSelectedRowModel={table.getSelectedRowModel}
        hasNextPage={table.getCanNextPage()}
        hasPreviousPage={table.getCanPreviousPage()}
        nextPage={table.nextPage}
        pageCount={table.getPageCount()}
        pageIndex={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        previousPage={table.previousPage}
        rerender={rerender}
        rowSelection={rowSelection}
        setPageIndex={table.setPageIndex}
        setPageSize={table.setPageSize}
        totalRows={table.getPrePaginationRowModel().rows.length}
        includeSelection={includeSelection ?? true}
        includePagination={includePagination ?? true}
      />
    </>
  );
};
